import { useState, useEffect, FC } from "react";
import { callErrorToast } from "../../../utilities/index.js";
import { AddressBookService as RegistryAddressBookService } from "../../../services/registry/addressBook.ts";
import { AddressBookService as LogisticAddressBookService } from "../../../services/logistic/addressBook.ts";
import { AddressBookService as ShipmentAddressBookService } from "../../../services/shipment/addressBook.ts";
import { ErrorResponse } from "../../../Models/Error.ts";
import { AddressBook } from "../../../Models/AddressBook.ts";
import {
  AddressBookDeleteContextValue,
  AddressBookDeleteProviderProps,
} from "./";
import { AddressBookDeleteContext } from "./";

/* ===========================================================================
 * SERVICE REGISTRY
 * Maps provider service type to the correct API service implementation.
 * =========================================================================== */
const services = {
  logistic: { addressBookService: new LogisticAddressBookService() },
  registry: { addressBookService: new RegistryAddressBookService() },
  shipment: { addressBookService: new ShipmentAddressBookService() },
};

/* ===========================================================================
 * PROVIDER: AddressBookDeleteProvider
 * =========================================================================== */

/**
 * @component AddressBookDeleteProvider
 * * Provider component responsible for managing the state and logic for batch deletion
 * of AddressBook entries.
 * * Responsibilities:
 * - Handling concurrent deletion of multiple AddressBook records.
 * - Managing loading states during API communication.
 * - Centralizing error reporting through toast notifications.
 * - Executing post-deletion callbacks.
 *
 * @param {AddressBookDeleteProviderProps} props - The component properties.
 * @returns {JSX.Element} React elements with the DeleteAddressBookContext provided.
 */
const AddressBookDeleteProvider: FC<AddressBookDeleteProviderProps> = ({
  children,
  data = [],
  callback,
  service = "registry",
}) => {
  const addressBookService = services[service].addressBookService;

  /* ---------------------------------------------------------------------------
   * STATE MANAGEMENT
   * ------------------------------------------------------------------------- */

  /** Holds any error response received during the API call. */
  const [error, setError] = useState<ErrorResponse<AddressBook> | null>(null);

  /** Indicates if the deletion process is currently in progress. */
  const [loader, setLoader] = useState<boolean>(false);

  /* ---------------------------------------------------------------------------
   * FUNCTION: deleteAddressBooks
   * ------------------------------------------------------------------------- */

  /**
   * Executes the deletion of all IDs in the 'data' array concurrently.
   * Uses Promise.allSettled to ensure all requests complete regardless of individual failures.
   *
   * @async
   * @returns {Promise<void>}
   */
  const deleteAddressBooks = async (): Promise<void> => {
    setLoader(true);

    try {
      // 1. Batch API Requests
      const results = await Promise.allSettled(
        data.map((id) => addressBookService.delete(id)),
      );

      const failed = results.filter(
        (r): r is PromiseRejectedResult => r.status === "rejected",
      );
      const success = results.filter(
        (r): r is PromiseFulfilledResult<void> => r.status === "fulfilled",
      );

      // 2. Error Handling logic
      if (failed.length > 0) {
        const firstError = failed[0].reason as ErrorResponse<AddressBook>;
        setError(firstError);

        // If at least one succeeded, we might still want to refresh the list
        if (success.length > 0 && callback) {
          callback();
        }
        return;
      }

      // 3. Complete Success
      if (callback) callback();
    } catch (unexpected) {
      const err: ErrorResponse<AddressBook> = {
        response: { data: {} as any },
      };
      setError(err);
    } finally {
      setLoader(false);
    }
  };

  /* ---------------------------------------------------------------------------
   * EFFECTS
   * ------------------------------------------------------------------------- */

  /**
   * Listens for error changes to trigger global toast notifications.
   */
  useEffect(() => {
    if (error) {
      callErrorToast(error);
    }
  }, [error]);

  /* ---------------------------------------------------------------------------
   * PROVIDER RETURN
   * ------------------------------------------------------------------------- */

  const contextValue: AddressBookDeleteContextValue = {
    loader,
    deleteAddressBooks,
    data,
    callback,
    service,
  };

  return (
    <AddressBookDeleteContext.Provider value={contextValue}>
      {children}
    </AddressBookDeleteContext.Provider>
  );
};

export { AddressBookDeleteProvider };
